import { useEffect } from "react";
import { Head, Link, router } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import { document_path, edit_document_path, edit_guide_path, edit_template_path, guide_path, new_document_path, new_guide_path, new_template_path, template_path } from "@/routes";

const configs = {
  template: { pluralLabel: "Skabeloner", singularLabel: "Skabelon", showPath: template_path, editPath: edit_template_path, newPath: new_template_path },
  document: { pluralLabel: "Dokumenter", singularLabel: "Dokument", showPath: document_path, editPath: edit_document_path, newPath: new_document_path },
  guide: { pluralLabel: "Guides", singularLabel: "Guide", showPath: guide_path, editPath: edit_guide_path, newPath: new_guide_path }
};

// Mirrors the AI status shown for product knowledge files and organization files.
function OpenaiStatusIcon({ item }) {
  if (item.openai_status === "failed") {
    const title = item.openai_sync_error || "Der opstod en fejl under synkroniseringen til AI.";

    return (
      <i className="bi bi-exclamation-triangle-fill text-danger fs-5" title={title} aria-label={title} />
    );
  }

  if (item.openai_status === "synced") {
    return (
      <i
        className="bi bi-check-circle-fill text-success fs-5"
        title="Synkroniseret til AI."
        aria-label="Synkroniseret til AI."
      />
    );
  }

  return (
    <span
      className="spinner-border spinner-border-sm text-secondary"
      role="status"
      title="Afventer synkronisering til AI."
      aria-label="Afventer synkronisering til AI."
    />
  );
}

function formatUpdatedAt(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

export default function ContentLibraryIndex({ content_kind: contentKind, content_label: contentLabel, items = [], can_edit: canEdit }) {
  const config = configs[contentKind] || configs.document;
  // Guides are not synced, so they carry no status and the column stays hidden.
  const showOpenaiStatus = items.some((item) => item.openai_status);
  const hasPendingItems = items.some((item) => item.openai_status === "pending");

  useEffect(() => {
    if (!hasPendingItems) return undefined;

    const timer = window.setInterval(() => {
      router.reload({ only: ["items"], preserveScroll: true });
    }, 3000);

    return () => window.clearInterval(timer);
  }, [hasPendingItems]);

  return (
    <>
      <Head title={config.pluralLabel} />

      <section className="workspace-content content-library-page">
        {items.length === 0 ? (
          <div className="workspace-empty">
            <h2 className="workspace-empty__title">Ingen {config.pluralLabel.toLowerCase()} endnu</h2>
            <p className="workspace-empty__copy">Opret den første {config.singularLabel.toLowerCase()} og giv den indhold i Markdown.</p>
            {canEdit ? <Link href={config.newPath()} className="btn btn-primary mt-3">Opret {config.singularLabel.toLowerCase()}</Link> : null}
          </div>
        ) : (
          <div className="table-responsive content-library-table-wrap">
            <table className="table content-library-table">
              <thead>
                <tr>
                  <th scope="col">Navn</th>
                  <th scope="col">Aktuel version</th>
                  <th scope="col">Versioner</th>
                  {contentKind === "document" ? <th scope="col">Skabelon</th> : null}
                  <th scope="col">Senest ændret</th>
                  {showOpenaiStatus ? <th scope="col">AI-status</th> : null}
                  <th scope="col" className="text-end">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Link href={config.showPath(item.id)} className="content-library-table__name">
                        {item.name || `Ny ${config.singularLabel.toLowerCase()}`}
                      </Link>
                    </td>
                    <td>
                      {item.version_number ? <span className="workspace-chip">v{item.version_number}</span> : <span className="text-muted">—</span>}
                    </td>
                    <td>{item.versions_count || 0}</td>
                    {contentKind === "document" ? (
                      <td>
                        {item.template_review?.needs_review ? (
                          <div className="content-library-table__template-review">
                            <span className="badge text-bg-warning">Der er ændringer i skabelonen</span>
                            <small>{item.template_review.template.name}</small>
                          </div>
                        ) : item.template_review?.template ? (
                          <span className="text-muted">Ingen opdatering</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    ) : null}
                    <td className="content-library-table__date">{formatUpdatedAt(item.updated_at)}</td>
                    {showOpenaiStatus ? (
                      <td><OpenaiStatusIcon item={item} /></td>
                    ) : null}
                    <td>
                      <div className="content-library-table__actions justify-content-end">
                        <Link href={config.showPath(item.id)} className="btn btn-sm btn-primary">Vis</Link>
                        {canEdit ? <Link href={config.editPath(item.id)} className="btn btn-sm btn-outline-secondary">Redigér</Link> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

ContentLibraryIndex.layout = (page) => {
  const { layout_header: layoutHeader } = page.props;

  return (
    <AuthLayout
      header={{
        breadcrumbs: [{ label: layoutHeader?.breadcrumb || "Bibliotek" }],
        title: layoutHeader?.title || "Bibliotek",
        actions: page.props.can_edit ? [{ href: configs[page.props.content_kind]?.newPath(), label: `Opret ${page.props.content_label?.toLowerCase() || "indhold"}` }] : []
      }}
    >
      {page}
    </AuthLayout>
  );
};
